/**
 * @file ParticleService.ts
 * @role Preset-driven particle bursts using texture-pooled Sprites. emit() spawns a preset's
 *       particles at a point (optionally into a caller-supplied target container); update(dtMs)
 *       advances them with the CLAMPED GameTicker dt (never raw rAF time, Invariant 22),
 *       interpolating alpha/scale over normalized lifetime via LifetimeCurve, and retires dead
 *       ones back into a TexturePool instead of destroying them. Presets live in
 *       particles.config.json (Zod-validated at construction).
 * @owns Live particles + their pooled Sprites, preset config, the shared particle TexturePool,
 *       and the spawn/advance/retire lifecycle.
 * @reads particles.config.json (or injected raw config); a render layer via attach(); an
 *       optional per-emit target layer.
 * @neverDoes Read raw rAF/Date for motion (uses injected dt). Decide outcomes. Block gameplay.
 *       Generate textures per-emit (generated once, lazily, at attach()).
 * @invariants 10 (config validated), 22 (dt-driven), 8 (render-only effect).
 * @see docs/PLAN.md (Particles), src/app/GameTicker.ts, src/core/particles/TexturePool.ts,
 *      src/core/particles/LifetimeCurve.ts
 */
import type { Container, Sprite } from 'pixi.js';
import { Texture } from 'pixi.js';
import { z } from 'zod';
import particlesConfigRaw from '@config/particles.config.json';
import { GameError } from '@core/errors/GameError';
import { sampleCurve, type CurveKeyframe } from '@core/particles/LifetimeCurve';
import { TexturePool } from '@core/particles/TexturePool';

const CurveKeyframeSchema = z.object({ t: z.number().min(0).max(1), v: z.number() });
const CurveSchema = z.array(CurveKeyframeSchema).min(1);

const DEFAULT_ALPHA_CURVE: CurveKeyframe[] = [
  { t: 0, v: 1 },
  { t: 1, v: 0 },
];
const DEFAULT_SCALE_CURVE: CurveKeyframe[] = [
  { t: 0, v: 1 },
  { t: 1, v: 1 },
];

const SpawnShapeSchema = z
  .discriminatedUnion('type', [
    z.object({ type: z.literal('point') }),
    z.object({ type: z.literal('burst'), startDeg: z.number(), spreadDeg: z.number().positive() }),
    z.object({
      type: z.literal('torus'),
      innerRadius: z.number().nonnegative(),
      outerRadius: z.number().positive(),
    }),
  ])
  .default({ type: 'point' });

const PresetSchema = z.object({
  count: z.number().int().positive(),
  color: z.number().int().nonnegative(),
  sizeMin: z.number().positive(),
  sizeMax: z.number().positive(),
  speedMin: z.number().nonnegative(),
  speedMax: z.number().nonnegative(),
  lifetimeMs: z.number().positive(),
  gravity: z.number(),
  spawnShape: SpawnShapeSchema,
  alphaCurve: CurveSchema.default(DEFAULT_ALPHA_CURVE),
  scaleCurve: CurveSchema.default(DEFAULT_SCALE_CURVE),
});
export const ParticlesConfigSchema = z.object({ presets: z.record(z.string(), PresetSchema) });

export type ParticlePreset = z.infer<typeof PresetSchema>;
export type ParticlesConfig = z.infer<typeof ParticlesConfigSchema>;
export type SpawnShape = z.infer<typeof SpawnShapeSchema>;

export function validateParticlesConfig(raw: unknown): ParticlesConfig {
  const parsed = ParticlesConfigSchema.safeParse(raw);
  if (!parsed.success) {
    throw new GameError('CONFIG_LOAD_FAILED', `Invalid particles config: ${parsed.error.message}`, {
      cause: parsed.error,
    });
  }
  return parsed.data;
}

interface Particle {
  sprite: Sprite;
  x: number;
  y: number;
  vx: number;
  vy: number;
  gravity: number;
  ageMs: number;
  lifeMs: number;
  baseSize: number;
  alphaCurve: readonly CurveKeyframe[];
  scaleCurve: readonly CurveKeyframe[];
}

/**
 * Generates a soft circular particle texture from an off-screen canvas. Deliberately avoids
 * `renderer.generateTexture()` — this codebase has no established pattern for threading a Pixi
 * Renderer down into service classes (GameApplication doesn't expose one, Bootstrapper doesn't
 * pass one to services). Guarded: some test DOM environments (this repo's jsdom canvas shim)
 * provide `getContext('2d')` but not a full 2D drawing API, so this falls back to `Texture.WHITE`
 * (a real, valid, poolable/tintable 1x1 white texture) rather than throwing — particle motion,
 * pooling, and curve behavior remain fully testable even where a *visually* circular texture
 * isn't available.
 */
function makeCircleTexture(diameter: number): Texture {
  try {
    const canvas = document.createElement('canvas');
    canvas.width = diameter;
    canvas.height = diameter;
    const ctx = canvas.getContext('2d');
    if (
      !ctx ||
      typeof ctx.beginPath !== 'function' ||
      typeof ctx.arc !== 'function' ||
      typeof ctx.fill !== 'function'
    ) {
      return Texture.WHITE;
    }
    ctx.beginPath();
    ctx.arc(diameter / 2, diameter / 2, diameter / 2, 0, Math.PI * 2);
    ctx.fillStyle = '#ffffff';
    ctx.fill();
    return Texture.from(canvas);
  } catch {
    return Texture.WHITE;
  }
}

const PARTICLE_TEXTURE_DIAMETER = 32;
const MIN_POOL_SIZE = 32;
const POOL_HEADROOM_MULTIPLIER = 2;

export class ParticleService {
  private readonly presets: Record<string, ParticlePreset>;
  private readonly particles: Particle[] = [];
  private layer: Container | undefined;
  private pool: TexturePool | undefined;
  private densityMultiplier = 1;

  constructor(raw: unknown = particlesConfigRaw) {
    this.presets = validateParticlesConfig(raw).presets;
  }

  attach(layer: Container): void {
    this.layer = layer;
    if (!this.pool) {
      const texture = makeCircleTexture(PARTICLE_TEXTURE_DIAMETER);
      this.pool = new TexturePool(texture, this.computePoolSize());
    }
  }

  /** Pool size is fixed once at attach() time, sized from the presets known at construction. */
  private computePoolSize(): number {
    const totalPresetCount = Object.values(this.presets).reduce((sum, p) => sum + p.count, 0);
    return Math.max(
      MIN_POOL_SIZE,
      Math.ceil(totalPresetCount * POOL_HEADROOM_MULTIPLIER * this.densityMultiplier),
    );
  }

  setDensityMultiplier(multiplier: number): void {
    this.densityMultiplier = Math.max(0, Math.min(1, multiplier));
    if (!this.pool) return;
    this.clear();
    this.pool.destroy();
    this.pool = new TexturePool(
      makeCircleTexture(PARTICLE_TEXTURE_DIAMETER),
      this.computePoolSize(),
    );
  }

  /**
   * Spawns `preset`'s particles at (x, y). By default they're added to the layer set by
   * attach(); pass `targetLayer` to route this call's particles into a different container
   * instead (e.g. a per-symbol local FX layer) without changing the service's default layer.
   */
  emit(preset: string, x: number, y: number, targetLayer?: Container): void {
    const def = this.presets[preset];
    const destination = targetLayer ?? this.layer;
    if (!def || !destination || !this.pool) return;

    const count = Math.round(def.count * this.densityMultiplier);
    for (let i = 0; i < count; i += 1) {
      const { originX, originY, angle } = resolveSpawn(def.spawnShape, x, y);
      const speed = def.speedMin + Math.random() * (def.speedMax - def.speedMin);
      const size = def.sizeMin + Math.random() * (def.sizeMax - def.sizeMin);

      const sprite = this.pool.acquire();
      sprite.tint = def.color;
      sprite.width = size;
      sprite.height = size;
      sprite.position.set(originX, originY);
      destination.addChild(sprite);

      this.particles.push({
        sprite,
        x: originX,
        y: originY,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        gravity: def.gravity,
        ageMs: 0,
        lifeMs: def.lifetimeMs,
        baseSize: size,
        alphaCurve: def.alphaCurve,
        scaleCurve: def.scaleCurve,
      });
    }
  }

  update(dtMs: number): void {
    if (this.particles.length === 0) return;
    const dt = dtMs / 1000;
    for (let i = this.particles.length - 1; i >= 0; i -= 1) {
      const p = this.particles[i]!;
      p.ageMs += dtMs;
      if (p.ageMs >= p.lifeMs) {
        this.pool?.release(p.sprite);
        this.particles.splice(i, 1);
        continue;
      }
      p.vy += p.gravity * dt;
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.sprite.position.set(p.x, p.y);

      const t = p.ageMs / p.lifeMs;
      p.sprite.alpha = sampleCurve(p.alphaCurve, t);
      const scale = sampleCurve(p.scaleCurve, t);
      p.sprite.width = p.baseSize * scale;
      p.sprite.height = p.baseSize * scale;
    }
  }

  get activeCount(): number {
    return this.particles.length;
  }

  /** Test/diagnostic hook mirroring TexturePool's own createdCount/freeCount (and
   *  AmbientFxService.poolStats' shape) — used to assert no per-spin pool growth. */
  get poolStats(): { createdCount: number; freeCount: number } {
    return { createdCount: this.pool?.createdCount ?? 0, freeCount: this.pool?.freeCount ?? 0 };
  }

  /**
   * Retire every live particle whose sprite is currently parented under `container`, via the
   * SAME release path `update()` uses for naturally-expired particles (pool release + splice
   * out of the live list) rather than a raw `destroy()`. Callers that spawn particles into a
   * caller-owned container (e.g. a per-symbol local FX layer that may be destroyed/reset out
   * from under this service) must call this BEFORE that container's children are destroyed, or
   * ParticleService.update() will next frame try to mutate an already-destroyed Sprite and its
   * TexturePool bookkeeping (checked-out count / free-list) will be left inconsistent.
   */
  retireIn(container: Container): void {
    if (this.particles.length === 0) return;
    for (let i = this.particles.length - 1; i >= 0; i -= 1) {
      const p = this.particles[i]!;
      if (p.sprite.parent !== container) continue;
      this.pool?.release(p.sprite);
      this.particles.splice(i, 1);
    }
  }

  clear(): void {
    for (const p of this.particles) this.pool?.release(p.sprite);
    this.particles.length = 0;
  }
}

function resolveSpawn(
  shape: SpawnShape,
  x: number,
  y: number,
): { originX: number; originY: number; angle: number } {
  switch (shape.type) {
    case 'burst': {
      const spreadRad = (shape.spreadDeg * Math.PI) / 180;
      const startRad = (shape.startDeg * Math.PI) / 180;
      const angle = startRad + (Math.random() - 0.5) * spreadRad;
      return { originX: x, originY: y, angle };
    }
    case 'torus': {
      const offsetAngle = Math.random() * Math.PI * 2;
      const radius = shape.innerRadius + Math.random() * (shape.outerRadius - shape.innerRadius);
      const originX = x + Math.cos(offsetAngle) * radius;
      const originY = y + Math.sin(offsetAngle) * radius;
      return { originX, originY, angle: Math.random() * Math.PI * 2 };
    }
    case 'point':
    default:
      return { originX: x, originY: y, angle: Math.random() * Math.PI * 2 };
  }
}
